import { getMatomo, getResolvedHref, loadScript } from './utils';

const defaultOptions = {
  debug: false,
  disableCookies: false,
  requireCookieConsent: false,
  enableHeartBeatTimer: false,
  enableLinkTracking: true,
  heartBeatTimerInterval: 15,
  requireConsent: false,
  trackInitialView: true,
  trackSiteSearch: false,
  trackerFileName: 'matmo',
  trackerUrl: undefined,
  trackerScriptUrl: undefined,
  userId: undefined,
  cookieDomain: undefined,
  domains: undefined,
  preInitActions: [],
  crossOrigin: undefined,
};

export const matomoKey = 'Matomo';

function trackMatomoSiteSearch(options, { keyword, category, resultsCount }) {
  const Matomo = getMatomo();

  if (options.debug) { console.debug(`[vue-matomo] Site Search ${ keyword}`); }

  Matomo.trackSiteSearch(keyword, category, resultsCount);
}

function trackMatomoPageView(options, to, from) {
  const Matomo = getMatomo();

  let title;
  let url;
  let referrerUrl;

  if (options.router) {
    url = getResolvedHref(options.router, to.fullPath);
    referrerUrl = from && from.fullPath
      ? getResolvedHref(options.router, from.fullPath)
      : undefined;

    if (to.meta.analyticsIgnore) {
      if (options.debug) { console.debug(`[vue-matomo] Ignoring ${ url}`); }
      return;
    }

    if (options.debug) { console.debug(`[vue-matomo] Tracking ${ url}`); }
    title = to.meta.title || url;
  }

  if (referrerUrl) {
    Matomo.setReferrerUrl(window.location.origin + referrerUrl);
  }
  if (url) {
    Matomo.setCustomUrl(window.location.origin + url);
  }

  Matomo.trackPageView(title);
}

function trackUserInteraction(options, to, from) {
  if (typeof options.trackSiteSearch === 'function') {
    const siteSearch = options.trackSiteSearch(to);
    if (siteSearch) {
      trackMatomoSiteSearch(options, siteSearch);
      return;
    }
  }
  trackMatomoPageView(options, to, from);
}

function initMatomo(Vue, options) {
  const Matomo = getMatomo();

  const version = Number(Vue.version.split('.')[0]);

  if (version > 2) {
    // eslint-disable-next-line no-param-reassign
    Vue.config.globalProperties.$piwik = Matomo;
    // eslint-disable-next-line no-param-reassign
    Vue.config.globalProperties.$matomo = Matomo;
    Vue.provide(matomoKey, Matomo);
  } else {
    // eslint-disable-next-line no-param-reassign
    Vue.prototype.$piwik = Matomo;
    // eslint-disable-next-line no-param-reassign
    Vue.prototype.$matomo = Matomo;
  }

  if (options.trackInitialView && options.router) {
    // Vue 3 must use currentRoute.value
    const currentRoute = options.router.currentRoute.value
      ? options.router.currentRoute.value
      : options.router.currentRoute;

    // Register first page view
    trackUserInteraction(options, currentRoute);
  }

  // Track page navigations if router is specified
  if (options.router) {
    options.router.afterEach((to, from) => {
      trackUserInteraction(options, to, from);

      if (options.enableLinkTracking) {
        Matomo.enableLinkTracking();
      }
    });
  }
}

function piwikExists() {
  // In case of TMS,  we load a first container_XXX.js
  // which triggers aynchronously the loading of the standard Piwik.js
  // this will avoid the error throwed in initMatomo when window.Piwik is undefined
  // if window.Piwik is still undefined when counter reaches 3000ms we reject and go to error

  // eslint-disable-next-line no-unused-vars
  return new Promise((resolve, reject) => {
    const checkInterval = 50;
    const timeout = 3000;
    const waitStart = Date.now();

    // eslint-disable-next-line consistent-return
    const interval = setInterval(() => {
      if (window.Piwik) {
        clearInterval(interval);

        return resolve();
      }

      if (Date.now() >= waitStart + timeout) {
        clearInterval(interval);

        throw new Error(`[vue-matomo]: window.Piwik undefined after waiting for ${timeout}ms`);
      }
    }, checkInterval);
  });
}

export default function install(Vue, setupOptions = {}) {
  const options = { ...defaultOptions, ...setupOptions };

  const {
    host, siteId, trackerFileName, trackerUrl, trackerScriptUrl,
  } = options;
  const trackerScript = trackerScriptUrl || `${host}/${trackerFileName}.js`;
  const trackerEndpoint = trackerUrl || `${host}/${trackerFileName}.php`;
  // eslint-disable-next-line no-underscore-dangle
  window._paq = window._paq || [];
  // eslint-disable-next-line no-underscore-dangle
  window._paq.push(['setTrackerUrl', trackerEndpoint]);
  // eslint-disable-next-line no-underscore-dangle
  window._paq.push(['setSiteId', siteId]);

  if (options.requireConsent) {
    // eslint-disable-next-line no-underscore-dangle
    window._paq.push(['requireConsent']);
  }

  if (options.userId) {
    // eslint-disable-next-line no-underscore-dangle
    window._paq.push(['setUserId', options.userId]);
  }

  if (options.enableLinkTracking) {
    // eslint-disable-next-line no-underscore-dangle
    window._paq.push(['enableLinkTracking']);
  }

  if (options.disableCookies) {
    // eslint-disable-next-line no-underscore-dangle
    window._paq.push(['disableCookies']);
  }

  if (options.requireCookieConsent) {
    // eslint-disable-next-line no-underscore-dangle
    window._paq.push(['requireCookieConsent']);
  }

  if (options.enableHeartBeatTimer) {
    // eslint-disable-next-line no-underscore-dangle
    window._paq.push(['enableHeartBeatTimer', options.heartBeatTimerInterval]);
  }

  if (options.cookieDomain) {
    // eslint-disable-next-line no-underscore-dangle
    window._paq.push(['setCookieDomain', options.cookieDomain]);
  }

  if (options.domains) {
    // eslint-disable-next-line no-underscore-dangle
    window._paq.push(['setDomains', options.domains]);
  }
  // eslint-disable-next-line no-underscore-dangle
  options.preInitActions.forEach((action) => window._paq.push(action));

  loadScript(trackerScript, options.crossOrigin)
    .then(() => piwikExists())
    .then(() => initMatomo(Vue, options))
    // eslint-disable-next-line consistent-return
    .catch((error) => {
      if (error.target) {
        return console.error(
          `[vue-matomo] An error occurred trying to load ${error.target.src}. `
          + 'If the file exists you may have an ad- or trackingblocker enabled.',
        );
      }

      console.error(error);
    });
}
